import React, { useState, useEffect, useCallback } from 'react';
import './ManageUGBatches.css';
import collegeConfig from '../../config/collegeConfig';
import { FaPlus, FaTimes, FaEdit } from 'react-icons/fa';

const emptyForm = { batchLabel: '', admissionYear: '', phase1Start: '', phase2Start: '', phase3Part1Start: '', phase3Part2Start: '' };

const ManageUGBatches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/batches`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setBatches(data.batches || []);
    } catch (err) {
      setError('Failed to load batches: ' + err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const toDateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (batch) => {
    setEditingId(batch._id);
    setForm({
      batchLabel: batch.batchLabel,
      admissionYear: batch.admissionYear,
      phase1Start: toDateInput(batch.phaseDates.phase1Start),
      phase2Start: toDateInput(batch.phaseDates.phase2Start),
      phase3Part1Start: toDateInput(batch.phaseDates.phase3Part1Start),
      phase3Part2Start: toDateInput(batch.phaseDates.phase3Part2Start)
    });
    setShowModal(true);
  };

  const submit = async () => {
    if (!form.batchLabel || !form.admissionYear || !form.phase1Start) {
      setError('Batch label, admission year and Phase 1 start date are required');
      return;
    }
    try {
      setSubmitting(true);
      const url = editingId
        ? `${collegeConfig.api.baseURL}/ug/batches/${editingId}`
        : `${collegeConfig.api.baseURL}/ug/batches`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(editingId ? 'Batch updated' : 'Batch created');
        setShowModal(false);
        fetchBatches();
      } else {
        setError(data.message || 'Failed to save batch');
      }
    } catch (err) {
      setError('Error connecting to server: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mugb-root">
      <div className="mugb-header">
        <h2>Manage UG Batches</h2>
        <button className="mugb-btn-primary" onClick={openCreate}><FaPlus /> New batch</button>
      </div>

      {error && <div className="mugb-alert mugb-alert-error">{error}</div>}
      {success && <div className="mugb-alert mugb-alert-success">{success}</div>}

      {loading ? (
        <div className="mugb-empty">Loading batches...</div>
      ) : batches.length === 0 ? (
        <div className="mugb-empty">No batches yet — create one to start assigning UG students.</div>
      ) : (
        <table className="mugb-table">
          <thead>
            <tr>
              <th>Batch</th><th>Admission Year</th><th>Phase 1</th><th>Phase 2</th><th>Phase 3 Pt I</th><th>Phase 3 Pt II</th><th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b._id}>
                <td className="mugb-label">{b.batchLabel}</td>
                <td>{b.admissionYear}</td>
                <td>{toDateInput(b.phaseDates.phase1Start) || '—'}</td>
                <td>{toDateInput(b.phaseDates.phase2Start) || '—'}</td>
                <td>{toDateInput(b.phaseDates.phase3Part1Start) || '—'}</td>
                <td>{toDateInput(b.phaseDates.phase3Part2Start) || '—'}</td>
                <td><button className="mugb-edit-btn" onClick={() => openEdit(b)}><FaEdit /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="mugb-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="mugb-modal" onClick={e => e.stopPropagation()}>
            <div className="mugb-modal-head">
              <h3>{editingId ? 'Edit batch' : 'New batch'}</h3>
              <FaTimes onClick={() => setShowModal(false)} />
            </div>
            <p className="mugb-modal-hint">
              Phase dates should be entered as they actually happen (e.g. once Phase 1 university exam results
              are declared) — not pre-filled from a fixed formula, since real academic calendars shift year to year.
            </p>

            <label className="mugb-form-label">Batch label</label>
            <input className="mugb-form-input" placeholder="e.g. MBBS 2025-26" value={form.batchLabel}
              onChange={e => setForm({ ...form, batchLabel: e.target.value })} />

            <label className="mugb-form-label">Admission year</label>
            <input type="number" className="mugb-form-input" value={form.admissionYear}
              onChange={e => setForm({ ...form, admissionYear: e.target.value })} />

            <label className="mugb-form-label">Phase 1 start date</label>
            <input type="date" className="mugb-form-input" value={form.phase1Start}
              onChange={e => setForm({ ...form, phase1Start: e.target.value })} />

            <label className="mugb-form-label">Phase 2 start date (fill in once known)</label>
            <input type="date" className="mugb-form-input" value={form.phase2Start}
              onChange={e => setForm({ ...form, phase2Start: e.target.value })} />

            <label className="mugb-form-label">Phase 3 Part I start date (fill in once known)</label>
            <input type="date" className="mugb-form-input" value={form.phase3Part1Start}
              onChange={e => setForm({ ...form, phase3Part1Start: e.target.value })} />

            <label className="mugb-form-label">Phase 3 Part II start date (fill in once known)</label>
            <input type="date" className="mugb-form-input" value={form.phase3Part2Start}
              onChange={e => setForm({ ...form, phase3Part2Start: e.target.value })} />

            <div className="mugb-modal-actions">
              <button className="mugb-btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
              <button className="mugb-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUGBatches;
